import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../../services/firebase";
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from "firebase/auth";
import { collection, doc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { refreshStudentsFromFirestore, getAllStudents } from "../../services/studentsService";
import { computeStudentFinance, parseMoneyAmount } from "../../utils/feeCalculator";
import StudentPortal from "./StudentPortal";
import jsPDF from "jspdf";
import { getFirebaseConfig } from "../../config/firebaseConfig";
import { classScheduleDocId } from "../../services/classScheduleService";

export default function StudentPortalPage() {
  const [user, setUser] = useState(null);
  const [students, setStudents] = useState(() => getAllStudents());
  const [student, setStudent] = useState(null);
  const [payments, setPayments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [scheduleEntries, setScheduleEntries] = useState([]);
  const [authError, setAuthError] = useState(null);
  const [linkingId, setLinkingId] = useState("");
  const provider = useMemo(() => new GoogleAuthProvider(), []);
  const cfg = useMemo(() => getFirebaseConfig(), []);
  const [showLinker, setShowLinker] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [scheduleMeta, setScheduleMeta] = useState(null);

  const staffProfile = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      const parsed = raw ? JSON.parse(raw) : null;
      const staffEmail = String(parsed?.email || "").toLowerCase().trim();
      const authEmail = String(user?.email || "").toLowerCase().trim();
      if (!staffEmail || !authEmail || staffEmail !== authEmail) return null;
      return parsed;
    } catch {
      return null;
    }
  }, [user?.email]);
  const canSwitchStudent = useMemo(() => {
    const r = String(staffProfile?.role || "").toLowerCase();
    const rs = Array.isArray(staffProfile?.roles) ? staffProfile.roles.map(x => String(x).toLowerCase()) : [];
    const has = (name) => r === name || rs.includes(name);
    return has("admin") || has("superadmin") || has("registrar") || has("finance");
  }, [staffProfile]);

  async function findStudentByEmail(email) {
    const raw = String(email || "").trim();
    const e = raw.toLowerCase();
    if (!raw) return null;
    const values = Array.from(new Set([raw, e]));
    const fields = ["studentEmail", "email", "guardianEmail", "parentEmail"];
    for (const v of values) {
      for (const f of fields) {
        try {
          const snap = await getDocs(query(collection(db, "students"), where(f, "==", v)));
          if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
        } catch {
          void 0;
        }
      }
    }
    return null;
  }

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
    });
    return () => unsubAuth();
  }, []);

  useEffect(() => {
    const unsubAnns = onSnapshot(collection(db, "announcements"), (snap) => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAnnouncements(rows);
    });
    let unsubStudents = null;
    (async () => {
      if (!canSwitchStudent) return;
      try {
        const list = await refreshStudentsFromFirestore();
        if (Array.isArray(list) && list.length) setStudents(list);
      } catch {
        void 0;
      }
      unsubStudents = onSnapshot(collection(db, "students"), (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setStudents(list);
      });
    })();
    return () => {
      unsubAnns();
      if (unsubStudents) unsubStudents();
    };
  }, [canSwitchStudent]);

  useEffect(() => {
    if (!user) {
      setStudent(null);
      setLinkError("");
      return;
    }
    const uid = user.uid;
    const email = String(user.email || "").toLowerCase();
    const saved = localStorage.getItem(`student_link_${uid}`);
    let linkedId = saved || "";
    (async () => {
      setLinkError("");
      if (!canSwitchStudent) {
        const s = await findStudentByEmail(email);
        if (s) {
          setStudent(s);
          return;
        }
        setStudent(null);
        setLinkError("No student record matched your Google account. Please contact the registrar to update your student email.");
        return;
      }
      if (!linkedId && email) {
        const matchByEmail = students.find(s => {
          const candidates = [
            s.studentEmail,
            s.email,
            s.guardianEmail,
            s.parentEmail
          ].filter(Boolean).map(x => String(x).toLowerCase().trim());
          return candidates.includes(email);
        });
        if (matchByEmail) linkedId = matchByEmail.id;
      }
      if (linkedId) {
        const s = students.find(st => String(st.id) === String(linkedId));
        if (s) setStudent(s);
      } else {
        setStudent(null);
      }
    })();
  }, [user, students, canSwitchStudent]);

  useEffect(() => {
    if (!student) {
      setPayments([]);
      setGrades([]);
      setScheduleEntries([]);
      setScheduleMeta(null);
      return;
    }
    const studentId = String(student.id || "");
    const paysQ = query(collection(db, "payments"), where("studentId", "==", studentId));
    const unsubPays = onSnapshot(paysQ, (snap) => {
      setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const gradeQ = query(collection(db, "grades"), where("studentId", "==", studentId));
    const unsubGrades = onSnapshot(gradeQ, (snap) => {
      setGrades(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const campus = String(student.campus || "").toLowerCase();
    const gradeLevel = String(student.gradeLevel || "");
    const secList = Array.isArray(student.sections)
      ? student.sections
      : String(student.section || student.sections || "").split(",").map(s => s.trim()).filter(Boolean);
    const sections = secList.map(s => String(s)).filter(Boolean);
    if (!campus || !gradeLevel || sections.length === 0) {
      setScheduleEntries([]);
      setScheduleMeta({ campus, gradeLevel, sections, tried: [] });
      return () => { unsubPays(); unsubGrades(); };
    }
    const tried = sections.map(section => classScheduleDocId({ campus, gradeLevel, section }));
    setScheduleMeta({ campus, gradeLevel, sections, tried });
    const unsubs = tried.slice(0, 5).map((schedId) => {
      const schedRef = doc(db, "class_schedules_current", schedId);
      return onSnapshot(schedRef, (snap) => {
        if (!snap.exists()) return;
        const d = snap.data() || {};
        const entries = Array.isArray(d.entries) ? d.entries : [];
        if (entries.length === 0) return;
        setScheduleEntries(entries);
      });
    });
    return () => {
      unsubPays();
      unsubGrades();
      unsubs.forEach(fn => fn());
    };
  }, [student]);

  const studentPayments = useMemo(() => {
    if (!student) return [];
    return payments.filter(p => String(p.studentId || p.student_id || "") === String(student.id));
  }, [payments, student]);

  const finance = useMemo(() => {
    if (!student) return { totalDue: 0, totalPaid: 0, outstanding: 0, nextDue: 0 };
    const mode = student.paymentMode || "Annual";
    return computeStudentFinance({ ...student, paymentMode: mode }, studentPayments, "Annual");
  }, [student, studentPayments]);

  function linkSelected() {
    if (!user || !linkingId) return;
    localStorage.setItem(`student_link_${user.uid}`, String(linkingId));
    const s = students.find(st => String(st.id) === String(linkingId));
    if (s) setStudent(s);
  }

  async function loginWithGoogle() {
    try {
      setAuthError(null);
      await signInWithPopup(auth, provider);
    } catch (e) {
      const code = e?.code || "";
      const msg = e?.message || String(e);
      setAuthError({ code, message: msg });
    }
  }
  function onExportSOA() {
    if (!student) return;
    const rows = studentPayments.sort((a,b)=>new Date(a.date)-new Date(b.date));
    const total = rows.reduce((s,p)=>s+parseMoneyAmount(p.amount),0);
    const doc = new jsPDF();
    const money = (n) => `PHP ${Number(n || 0).toLocaleString()}`;
    doc.setFontSize(16);
    doc.text("Statement of Account", 14, 18);
    doc.setFontSize(12);
    doc.text(`Student ID: ${student.id}`, 14, 28);
    doc.text(`Name: ${[student.firstname, student.middlename, student.lastname].filter(Boolean).join(" ").trim() || (student.fullname||"")}`, 14, 36);
    doc.text(`Campus: ${student.campus||""}`, 14, 44);
    doc.text("History of Payments:", 14, 56);
    let y = 64;
    rows.forEach(r => {
      const line = `${r.date}  •  ${r.method}  •  ${r.reference || "-"}  •  ${money(parseMoneyAmount(r.amount))}`;
      doc.text(line, 14, y);
      y += 8;
    });
    doc.text(`Total Paid: ${money(total)}`, 14, y + 6);
    const campusKey = String(student.campus || "").toLowerCase();
    const preparedBy = campusKey.includes("bulacan") ? "Prepared by: Emerenciana L. Ventura" : "Prepared by: Catalina T. Basa";
    doc.text(preparedBy, 14, y + 22);
    doc.save(`SOA_${student.id}.pdf`);
  }

  return (
    <div className="container py-4">
      <h3 className="mb-3">VMSC Student Portal</h3>
      <div className="small text-muted mb-2">Firebase Project: {cfg.projectId} • Auth Domain: {cfg.authDomain}</div>
      {scheduleMeta && (
        <div className="small text-muted mb-2">
          Schedule key: {String(scheduleMeta.campus || "")} • {String(scheduleMeta.gradeLevel || "")} • {Array.isArray(scheduleMeta.sections) ? scheduleMeta.sections.join(", ") : ""} • Tried: {Array.isArray(scheduleMeta.tried) ? scheduleMeta.tried.join(", ") : ""}
        </div>
      )}
      {!user ? (
        <>
          {authError && (
            <div className="alert alert-warning">
              <div className="fw-bold">Sign-in failed: {authError.code || "error"}</div>
              <div className="small">{authError.message}</div>
              {String(authError.code || "").includes("unauthorized-domain") && (
                <ul className="mt-2 mb-0">
                  <li>Add your domain to Firebase Auth → Settings → Authorized domains: vmsc-school-website-and-sms.web.app and portal.vmsc.education</li>
                  <li>Ensure Google provider is enabled in Firebase Auth</li>
                  <li>Reload this page after updating settings</li>
                </ul>
              )}
            </div>
          )}
          <button className="btn btn-primary" onClick={loginWithGoogle}>
            Sign in with Google
          </button>
        </>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <div className="small text-muted">Signed in as</div>
              <div>{user.email}</div>
            </div>
            <div className="d-flex gap-2">
              {canSwitchStudent && student && (
                <button className="btn btn-outline-primary btn-sm" onClick={() => setShowLinker(!showLinker)}>
                  {showLinker ? "Close switch" : "Switch student"}
                </button>
              )}
              <button className="btn btn-outline-secondary btn-sm" onClick={() => signOut(auth)}>Sign out</button>
            </div>
          </div>
          {linkError && !canSwitchStudent && (
            <div className="alert alert-warning">{linkError}</div>
          )}
          {canSwitchStudent && (!student || showLinker) && (
            <div className="alert alert-info">
              <div className="mb-2">Select your student ID to link:</div>
              <div className="d-flex gap-2">
                <select className="form-select" value={linkingId} onChange={e => setLinkingId(e.target.value)}>
                  <option value="">Select student</option>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>{s.id} • {s.lastname}, {s.firstname} • {s.campus}</option>
                  ))}
                </select>
                <button className="btn btn-primary" onClick={linkSelected} disabled={!linkingId}>Link</button>
              </div>
            </div>
          )}
          {student && (
            <StudentPortal
              student={{
                id: student.id,
                name: [student.firstname, student.middlename, student.lastname].filter(Boolean).join(" ").trim() || (student.fullname || ""),
                photoUrl: student.photoUrl || "",
                level: student.gradeLevel,
                campus: student.campus,
                email: student.studentEmail || "",
                paymentMode: student.paymentMode || "Annual",
                totalDue: finance.totalDue,
                totalPaid: finance.totalPaid,
                outstanding: finance.outstanding,
                nextDue: finance.nextDue,
                nextLabel: finance.nextLabel,
                payments: studentPayments
              }}
              announcements={(() => {
                const today = new Date().toISOString().slice(0,10);
                const filtered = announcements.filter(a => {
                  const campusOk = !student.campus || String(a.campus||"").toLowerCase()===String(student.campus||"").toLowerCase();
                  const gradeOk = !a.gradeLevel || String(a.gradeLevel||"").trim().toLowerCase()===String(student.gradeLevel||"").trim().toLowerCase();
                  const secs = Array.isArray(a.sections) ? a.sections : [];
                  const secsLC = secs.map(s => String(s).trim().toLowerCase());
                  const secList = Array.isArray(student.sections) ? student.sections : String(student.section||"").split(",").map(s=>s.trim()).filter(Boolean);
                  const secListLC = secList.map(s => s.toLowerCase());
                  const sectionOk = secsLC.length===0 || secsLC.some(x => secListLC.includes(x));
                  const startOk = !a.startsAt || today >= a.startsAt;
                  const endOk = !a.endsAt || today <= a.endsAt;
                  return campusOk && gradeOk && sectionOk && startOk && endOk;
                });
                return filtered.sort((x,y)=>{
                  const px = x.pinned ? 0 : 1, py = y.pinned ? 0 : 1;
                  if (px !== py) return px - py;
                  const hx = x.priority === "high" ? 0 : 1, hy = y.priority === "high" ? 0 : 1;
                  if (hx !== hy) return hx - hy;
                  return String(y.publishedAt||y.startsAt||"").localeCompare(String(x.publishedAt||x.startsAt||""));
                });
              })()}
              grades={grades.filter(g => String(g.studentId||"")===String(student.id))}
              schedule={scheduleEntries}
              onExportSOA={onExportSOA}
            />
          )}
        </>
      )}
    </div>
  );
}
